/**
 * Turning `@Ana` into a mention Skool will actually render.
 *
 * Skool stores a mention as an object link — `[@Ana](obj://user/<external_id>)` — and
 * renders anything else as literal text. Every layer here could already READ that
 * (`skoolContent.ts` parses it, the backend's `skool_markdown.py` strips it, the
 * moderation inbox matches on it to find mentions of a moderator); nothing could
 * WRITE one. So a suggested reply that opened "Hey @Ana" posted as the characters
 * `@Ana`, Skool left it inert, and the owner had to retype the name on Skool to make
 * it a link. That is the whole of the reported bug.
 *
 * WHY THIS RUNS ON THE WAY OUT, NOT IN THE BOX. The owner writes and reads `@Ana`;
 * turning the textarea into a rich editor to show `[@Ana](obj://user/…)` would be a
 * worse thing to read and a much larger change. The rewrite happens at the two places
 * that hand text to Skool, so the draft, the outbox row and what the owner saw all
 * stay the plain thing they typed.
 *
 * WHY THE REWRITE TAKES CANDIDATES RATHER THAN SEARCHING. A mention needs an
 * external_id, and only the caller knows which people are even in the conversation —
 * the thread's author and commenters, or the two ends of a DM. Guessing from a global
 * directory would let "@Sam" address a stranger who happens to share a name with the
 * person being replied to. The PICKER below is what widens that set, on purpose and
 * under the owner's finger rather than behind their back.
 */

import { ui } from '../host';

/**
 * Whether a DM may carry a mention. It may not.
 *
 * Skool renders mentions in posts and comments and NOT in direct messages — tested
 * against the real product, not inferred. Rewriting there would be actively worse than
 * doing nothing: the recipient would receive the literal characters
 * `[@Ana](obj://user/9f3…)` where the sender typed `@Ana`, so the feature would damage
 * the message it was meant to improve.
 *
 * A flag rather than a deletion because this is Skool's limitation, not ours, and the
 * client half is written and tested. Flipping it to `true` restores the picker in the
 * DM composer and the rewrite on the DM send path — the two readers below and in
 * `lib/skool.ts` are the whole of it.
 */
export const MENTIONS_IN_DMS = false;

/** Someone who can be mentioned here, and the id Skool files the mention under. */
export interface MentionCandidate {
  /** Skool's user id. A candidate without one cannot be linked and is skipped. */
  externalId: string | null | undefined;
  name: string | null | undefined;
  handle: string | null | undefined;
  /** For the picker only; the rewrite never reads it. */
  pictureUrl?: string | null;
}

/**
 * Who "@" could mean, from the community roster.
 *
 * A WORKAROUND, deliberately, and issue #169 tracks whether to pay it off. Skool's own
 * mention menu searches the live membership of the place you are writing in. This
 * searches OUR MIRROR of one community's roster, which differs in three ways worth
 * knowing before trusting it:
 *
 *   1. It is only as fresh as the last roster sync — someone who joined since will
 *      not appear, and the owner will type a name that stays inert.
 *   2. It is COMMUNITY-SCOPED, and a DM is not. The panel searches whichever
 *      community is selected, which is usually right (that is where the conversation
 *      came from) and is not guaranteed to be.
 *   3. It ranks by whatever `?q=` matches, not by who is in this thread. The caller
 *      merges the thread's own participants in front for that reason.
 *
 * `fields=` is narrowed on purpose: the default member row carries membership, stats,
 * journey and activity blocks, and a menu that redraws on every keystroke has no use
 * for any of it.
 */
export async function searchMentionCandidates(
  skoolId: number,
  q: string,
  limit = 6,
): Promise<MentionCandidate[]> {
  const body = await ui().apiGet<{ items?: RosterRow[] }>(`/v1/communities/${skoolId}/members`, {
    q,
    limit,
    fields: ['external_id', 'handle', 'name', 'picture_profile'],
  });
  return (body.items ?? [])
    .filter((m) => m.external_id)
    .map((m) => ({
      externalId: m.external_id,
      name: m.name,
      handle: m.handle,
      pictureUrl: m.picture_profile ?? null,
    }));
}

interface RosterRow {
  external_id: string | null;
  handle: string | null;
  name: string | null;
  picture_profile?: string | null;
}

/** Any markdown link, including a mention that is ALREADY one. These regions are
 *  copied through untouched: re-linking `[@Ana](obj://user/1)` would nest brackets and
 *  produce something Skool renders as neither a link nor a name. */
const LINK = /\[[^\]]*\]\([^)]*\)/g;

/** True when `ch` may not sit against a mention. A name has to start at a word
 *  boundary and end at one, or `hello@example.com` becomes a mention of Example and
 *  `@Anabel` matches the candidate "Ana". */
function isWordChar(ch: string | undefined): boolean {
  return ch != null && /[A-Za-z0-9_]/.test(ch);
}

/** The aliases one person answers to, longest first.
 *
 *  ORDER IS THE WHOLE CORRECTNESS ARGUMENT: with candidates "Ana" and "Ana Lucia",
 *  matching the short one first turns "@Ana Lucia" into a link to Ana followed by the
 *  stray word "Lucia". Longest-first makes the greedy match the right one. */
function aliasesOf(candidates: MentionCandidate[]): { alias: string; anchor: string; id: string }[] {
  const out: { alias: string; anchor: string; id: string }[] = [];
  for (const c of candidates) {
    const id = c.externalId?.trim();
    if (!id) continue;
    const name = c.name?.trim();
    const handle = c.handle?.trim();
    // The anchor Skool shows. Their real name where we have one; the handle otherwise,
    // which is what Skool itself falls back to.
    const anchor = name || (handle ? `@${handle}` : '');
    if (!anchor) continue;
    if (name) out.push({ alias: name, anchor, id });
    if (handle && handle !== name) out.push({ alias: handle, anchor, id });
  }
  return out.sort((a, b) => b.alias.length - a.alias.length);
}

/**
 * The alias mentioned at `i`, or null.
 *
 * ONE MATCHER, because two things ask this question and they must never disagree:
 * `linkSegment` rewrites what it finds, and `isOnlyMentions` removes what it finds to
 * decide whether anything was actually written. A composer that thought a mention was
 * prose — or prose a mention — would offer the wrong control for the box's contents.
 */
function mentionAt(
  text: string,
  i: number,
  aliases: ReturnType<typeof aliasesOf>,
): ReturnType<typeof aliasesOf>[number] | null {
  if (text[i] !== '@' || isWordChar(text[i - 1])) return null;
  const rest = text.slice(i + 1);
  return (
    aliases.find((a) => {
      if (!rest.toLowerCase().startsWith(a.alias.toLowerCase())) return false;
      // ...and it must END at a boundary too, so "Ana" does not match inside "Anabel".
      return !isWordChar(rest[a.alias.length]);
    }) ?? null
  );
}

/** One segment of plain text — no markdown links inside — with every `@alias` linked. */
function linkSegment(text: string, aliases: ReturnType<typeof aliasesOf>): string {
  let out = '';
  let i = 0;
  while (i < text.length) {
    const hit = mentionAt(text, i, aliases);
    if (!hit) {
      out += text[i];
      i += 1;
      continue;
    }
    // The anchor is OURS, not what was typed: Skool shows the person's real name, so
    // "@ana" and "@Ana" both come out as the name they are registered under.
    const shown = hit.anchor.startsWith('@') ? hit.anchor : `@${hit.anchor}`;
    out += `[${shown}](obj://user/${hit.id})`;
    i += 1 + hit.alias.length;
  }
  return out;
}

/**
 * Rewrite every plain `@alias` in `text` as a Skool mention of that candidate.
 *
 * Returns the text unchanged when there is nothing to do — no candidates, no ids, or
 * no `@` that matches one — so it is safe to call on every write regardless of whether
 * the body mentions anybody.
 */
export function linkMentions(text: string, candidates: MentionCandidate[]): string {
  if (!text || candidates.length === 0) return text ?? '';
  const aliases = aliasesOf(candidates);
  if (aliases.length === 0) return text;

  let out = '';
  let last = 0;
  // Walk the existing links so their innards are never rewritten, and only the plain
  // text between them is scanned.
  for (const m of text.matchAll(LINK)) {
    const at = m.index ?? 0;
    out += linkSegment(text.slice(last, at), aliases) + m[0];
    last = at + m[0].length;
  }
  return out + linkSegment(text.slice(last), aliases);
}

/**
 * Has anything actually been WRITTEN, or is the box just names?
 *
 * A reply box that opens addressed to someone starts with `@Name ` in it, and every
 * control that used to ask "is it empty" was really asking this. Emptying the box by
 * hand, or leaving only a mention picked from the menu, both mean the same thing: no
 * answer yet, so offer to draft one.
 *
 * Existing links are dropped first, so a body already rewritten to
 * `[@Name](obj://user/…)` counts the same as the plain `@Name` it came from.
 */
export function isOnlyMentions(text: string, candidates: MentionCandidate[]): boolean {
  const plain = (text ?? '').replace(LINK, ' ');
  if (!plain.trim()) return true;
  const aliases = aliasesOf(candidates);
  let rest = '';
  let i = 0;
  while (i < plain.length) {
    const hit = aliases.length ? mentionAt(plain, i, aliases) : null;
    if (hit) {
      i += 1 + hit.alias.length;
      continue;
    }
    rest += plain[i];
    i += 1;
  }
  return !rest.trim();
}
